import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Meta } from '@jetopolis/shared';
import { api } from '../../app/api/client';
import { authClient } from '../../app/auth';
import { useAuth } from '../../app/stores/auth';
import { useOverlays } from '../../app/stores/overlays';
import { Button } from '../../ui/Button';
import { Modal } from '../../ui/Modal';

const OTP_TTL_MINUTES = 5;

/**
 * Вход и привязка аккаунта.
 *
 * Гость привязывается через signIn.emailOtp и signIn.social, а НЕ через
 * linkSocial: хук привязки в ядре better-auth срабатывает на путях /sign-in/*
 * и /callback/*, но не на /link-social - оттуда прогресс не переехал бы.
 *
 * Показываются только те способы входа, у которых на стенде есть секреты.
 * Кнопка, ведущая в ошибку конфигурации, - мёртвый элемент, а инвариант 9
 * их запрещает.
 */
export function AuthModal() {
  const { t } = useTranslation();
  const close = useOverlays((s) => s.close);
  const refresh = useAuth((s) => s.refresh);

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  // Пока состав способов неизвестен, не рисуем ни одного: показать кнопку и
  // тут же её убрать хуже, чем на долю секунды показать пусто.
  const [methods, setMethods] = useState<Meta['auth'] | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setMethods((await api.get<Meta>('/v1/meta')).auth);
      } catch {
        setMethods({ emailOtp: false, google: false, discord: false });
      }
    })();
  }, []);

  const sendCode = async (): Promise<void> => {
    setBusy(true);
    setErrorKey(null);
    const { error } = await authClient.emailOtp.sendVerificationOtp({ email, type: 'sign-in' });
    setBusy(false);
    if (error) {
      setErrorKey('errors.common.internal');
      return;
    }
    setSent(true);
  };

  const verify = async (): Promise<void> => {
    setBusy(true);
    setErrorKey(null);
    const { error } = await authClient.signIn.emailOtp({ email, otp });
    setBusy(false);
    if (error) {
      setErrorKey('errors.auth.otpInvalid');
      return;
    }
    await refresh();
    close('auth');
  };

  const nothingAvailable = methods !== null && !methods.emailOtp && !methods.google;

  return (
    <Modal
      title={t('auth.modal.title')}
      onClose={() => {
        close('auth');
      }}
    >
      <p className="mb-4 text-[12px] text-mut">{t('auth.modal.subtitle')}</p>

      {nothingAvailable && <p className="mb-3 text-[12px] text-mut">{t('auth.noMethods')}</p>}

      {methods?.emailOtp === true && (
        <>
          <label
            className="mb-1 block text-[10px] uppercase tracking-wider text-dim"
            htmlFor="jt-email"
          >
            {t('auth.email.label')}
          </label>
          <input
            id="jt-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            placeholder={t('auth.email.placeholder')}
            className="mb-3 min-h-[var(--spacing-touch)] w-full rounded-[6px] border border-line2 bg-panel2 px-3 text-[13px] text-txt outline-none focus:border-amber"
          />

          {sent && (
            <>
              <p className="mb-2 text-[11px] text-cyan">{t('auth.email.sent', { email })}</p>
              <label
                className="mb-1 block text-[10px] uppercase tracking-wider text-dim"
                htmlFor="jt-otp"
              >
                {t('auth.otp.label')}
              </label>
              <input
                id="jt-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otp}
                onChange={(e) => {
                  setOtp(e.target.value.replace(/\D/g, ''));
                }}
                className="tabular mb-1 min-h-[var(--spacing-touch)] w-full rounded-[6px] border border-line2 bg-panel2 px-3 text-[16px] tracking-[0.4em] text-txt outline-none focus:border-amber"
              />
              <p className="mb-3 text-[10px] text-dim">
                {t('auth.otp.hint', { minutes: OTP_TTL_MINUTES })}
              </p>
            </>
          )}
        </>
      )}

      {errorKey !== null && <p className="mb-3 text-[12px] text-red">{t(errorKey)}</p>}

      <div className="flex flex-col gap-2">
        {methods?.emailOtp === true &&
          (sent ? (
            <Button
              variant="primary"
              onClick={() => {
                void verify();
              }}
              disabledReason={
                otp.length !== 6 || busy
                  ? t('auth.otp.hint', { minutes: OTP_TTL_MINUTES })
                  : undefined
              }
            >
              {t('auth.otp.verify')}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => {
                void sendCode();
              }}
              disabledReason={!email.includes('@') || busy ? t('auth.email.label') : undefined}
            >
              {t('auth.email.send')}
            </Button>
          ))}

        {methods?.google === true && (
          <Button
            onClick={() => {
              void authClient.signIn.social({
                provider: 'google',
                callbackURL: window.location.href,
              });
            }}
          >
            {t('auth.social.google')}
          </Button>
        )}
      </div>
    </Modal>
  );
}
